import React from 'react';
import './TestimonialsSection.css';

const {
  LANDING_TESTIMONIALS,
  TESTIMONIAL_SECTION_DEFAULTS,
  TESTIMONIAL_SECTION_MODES,
  getPublishedTestimonials,
} = require('../../seo/testimonials');

const SUPPORTED_MODES = new Set(Object.values(TESTIMONIAL_SECTION_MODES));

function resolveMode(mode) {
  return SUPPORTED_MODES.has(mode) ? mode : TESTIMONIAL_SECTION_DEFAULTS.mode;
}

function TestimonialCard({ testimonial, index, isDuplicate = false }) {
  const hasAttribution = Boolean(testimonial.author || testimonial.role || testimonial.source || testimonial.sourceUrl);

  return (
    <li className="testimonials-item" style={{ '--testimonial-index': index }}>
      <figure className="testimonials-card">
        <blockquote className="testimonials-quote">
          <p>{testimonial.quote}</p>
        </blockquote>
        {hasAttribution && (
          <figcaption className="testimonials-author">
            {testimonial.author && <strong>{testimonial.author}</strong>}
            {testimonial.role && <span>{testimonial.role}</span>}
            {testimonial.sourceUrl ? (
              <a
                href={testimonial.sourceUrl}
                target="_blank"
                rel="noreferrer"
                tabIndex={isDuplicate ? -1 : undefined}
              >
                {testimonial.source || 'Font'}
              </a>
            ) : (
              testimonial.source && <span>{testimonial.source}</span>
            )}
          </figcaption>
        )}
      </figure>
    </li>
  );
}

function TestimonialsList({ testimonials, className, ariaHidden = false }) {
  return (
    <ul className={className} aria-hidden={ariaHidden ? 'true' : undefined}>
      {testimonials.map((testimonial, index) => (
        <TestimonialCard
          key={`${testimonial.author}-${testimonial.quote}-${index}`}
          testimonial={testimonial}
          index={index}
          isDuplicate={ariaHidden}
        />
      ))}
    </ul>
  );
}

function TestimonialsMarquee({ testimonials }) {
  return (
    <div className="testimonials-marquee" aria-label={TESTIMONIAL_SECTION_DEFAULTS.ariaLabel}>
      <div className="testimonials-marquee-track">
        <TestimonialsList testimonials={testimonials} className="testimonials-marquee-list" />
        <TestimonialsList testimonials={testimonials} className="testimonials-marquee-list" ariaHidden />
      </div>
    </div>
  );
}

function TestimonialsStatic({ testimonials }) {
  return (
    <TestimonialsList testimonials={testimonials} className="testimonials-static-list" />
  );
}

function TestimonialsFade({ testimonials }) {
  return (
    <TestimonialsList testimonials={testimonials} className="testimonials-fade-list" />
  );
}

function TestimonialsBody({ mode, testimonials }) {
  if (mode === TESTIMONIAL_SECTION_MODES.STATIC) {
    return <TestimonialsStatic testimonials={testimonials} />;
  }

  if (mode === TESTIMONIAL_SECTION_MODES.FADE) {
    return <TestimonialsFade testimonials={testimonials} />;
  }

  return <TestimonialsMarquee testimonials={testimonials} />;
}

function TestimonialsSection({
  testimonials = LANDING_TESTIMONIALS,
  mode = TESTIMONIAL_SECTION_DEFAULTS.mode,
  kicker = TESTIMONIAL_SECTION_DEFAULTS.kicker,
  title = TESTIMONIAL_SECTION_DEFAULTS.title,
}) {
  const publishedTestimonials = getPublishedTestimonials(testimonials);

  if (!publishedTestimonials.length) {
    return null;
  }

  const resolvedMode = resolveMode(mode);

  return (
    <section
      className={`testimonials-section testimonials-section-${resolvedMode}`}
      aria-labelledby="testimonials-section-title"
      data-testimonials-mode={resolvedMode}
    >
      <div className="testimonials-section-heading">
        <p className="testimonials-section-kicker">{kicker}</p>
        <h2 id="testimonials-section-title" className="testimonials-section-title">
          {title}
        </h2>
      </div>

      <TestimonialsBody mode={resolvedMode} testimonials={publishedTestimonials} />
    </section>
  );
}

export default TestimonialsSection;
